import { useMemo } from "react";

import { WidgetFrame } from "@/components/layout/WidgetFrame";
import {
    WidgetWithOptionalQueryParameters,
    WidgetWithQueryParameters,
} from "@/components/layout/WidgetFrame/widget.types";
import { Link } from "@/components/ui/Link";
import { ClampedText } from "@/components/ui/TextClamped";
import { Tooltip } from "@/components/ui/Tooltip";
import { Column, createDataSortableColumn, useSort } from "@/components/ui/table-final-saviour/Table";
import { useSearch } from "@/components/ui/table-final-saviour/Table/hooks/useSearch";
import { ServiceHistoryExpandedContent } from "@/components/widgets/tables/service-history/ExpandedContent/ServiceHistoryExpandedContent";
import { useDataSource } from "@/features/data-source/useDataSource";
import { routes } from "@/routes";
import { generatePath, useGenerateDevicePath } from "@/routes/routes.utils";

type ServiceHistoryTechnicianDataRow = {
    Date: string;
    DeviceType: string;
    OrderNumber: string;
    Expanded: string;
    Label: string;
    Duration: number;
    Diagnosis1: string;
    DevicePageType: string;
    TechnicianCode: string;
    Diagnosis2: string;
    Diagnosis3: string;
    Diagnosis4: string;
    DescriptionOfWork: string;
    CustomerName: string;
    CustomerNumber: string;
    FarmCount: string;
    FarmLicenseKey: string;
    DeviceSerialNumber: string;
    DeviceLDNSN: string;
    TechnicianName: string;
};

type Props = WidgetWithQueryParameters<"dateFrom" | "dateTo"> &
    WidgetWithOptionalQueryParameters<"customerNumber" | "deviceSerialNumber" | "technicianCode">;

const ServiceHistoryWidget = (props: Props) => {
    const { queryParameters } = props;
    const generateDevicePath = useGenerateDevicePath();

    const dataSourceParameters = useMemo(
        () => ({
            ...queryParameters,
            customerNumber: queryParameters.customerNumber ?? "",
            deviceSerialNumber: queryParameters.deviceSerialNumber ?? "",
            technicianCode: queryParameters.technicianCode ?? "",
        }),
        [queryParameters],
    );

    const dataSource = useDataSource<ServiceHistoryTechnicianDataRow[]>(
        "service-history-technician",
        dataSourceParameters,
    );
    const { data = [] } = dataSource;

    const { sortControls, applySort } = useSort<ServiceHistoryTechnicianDataRow>({
        initialSortConfig: {
            key: "Date",
            dataType: "date",
            order: "desc",
        },
    });

    const columns = useMemo(
        () =>
            [
                createDataSortableColumn({
                    dataKey: "Date",
                    title: "Date",
                    dataType: "date",
                    width: 100,
                    ...sortControls,
                }),
                createDataSortableColumn({
                    dataKey: "DeviceType",
                    title: "Device Type",
                    dataType: "string",
                    width: 120,
                    ...sortControls,
                }),
                createDataSortableColumn({
                    dataKey: "OrderNumber",
                    title: "Order Number",
                    dataType: "string",
                    width: 130,
                    ...sortControls,
                }),
                createDataSortableColumn({
                    dataKey: "Label",
                    title: "Order Type",
                    dataType: "string",
                    width: 120,
                    ...sortControls,
                }),
                createDataSortableColumn({
                    dataKey: "Duration",
                    title: "Duration",
                    dataType: "number",
                    width: 90,
                    align: "right",
                    ...sortControls,
                }),
                createDataSortableColumn({
                    dataKey: "Diagnosis1",
                    title: "Diagnosis 1",
                    dataType: "string",
                    width: 300,
                    getCellValue: ({ row }) => (
                        <Tooltip content={row.Diagnosis1}>
                            <ClampedText $maxRows={4}>{row.Diagnosis1}</ClampedText>
                        </Tooltip>
                    ),
                    ...sortControls,
                }),
                createDataSortableColumn({
                    dataKey: "CustomerName",
                    title: "Customer",
                    dataType: "string",
                    width: 130,
                    getCellValue: ({ row }) => {
                        const linkPath = generatePath(routes.customerDetail, {
                            params: {
                                customerNumber: row.CustomerNumber,
                            },
                            searchParams: {
                                farmLicenseKey: row.FarmLicenseKey,
                            },
                        });
                        return linkPath ? <Link to={linkPath}>{row.CustomerName}</Link> : row.CustomerName;
                    },
                    ...sortControls,
                }),
                createDataSortableColumn({
                    dataKey: "DeviceLDNSN",
                    title: "LDN / SN",
                    dataType: "string",
                    width: 150,
                    getCellValue: ({ row }) => {
                        const linkPath = generateDevicePath({
                            customerNumber: row.CustomerNumber,
                            deviceSerialNumber: row.DeviceSerialNumber,
                            deviceType: row.DeviceType,
                            devicePageType: row.DevicePageType,
                        });
                        return linkPath ? <Link to={linkPath}>{row.DeviceLDNSN}</Link> : row.DeviceLDNSN;
                    },
                    ...sortControls,
                }),
            ] as Column<ServiceHistoryTechnicianDataRow>[],
        [sortControls, generateDevicePath],
    );

    const { searchValue, setSearchValue, applySearch } = useSearch({ columns });

    const processedData = useMemo(() => {
        return applySort(applySearch(data));
    }, [data, applySort, applySearch]);

    return (
        <>
            <WidgetFrame.Title title={"Service History"} icon={"lely-icon-tasks"}>
                <WidgetFrame.Title.Search value={searchValue} setValue={setSearchValue} />
                <WidgetFrame.Title.Information pageId={233996303} lastImportType={"middleware"} />
            </WidgetFrame.Title>
            <WidgetFrame.TableExpandable
                columns={columns}
                dataSource={{ ...dataSource, data: processedData }}
                maxHeight={600}
                ExpandedContent={ServiceHistoryExpandedContent}
                getRowId={(row) => row.OrderNumber}
            />
        </>
    );
};

export { ServiceHistoryWidget };
